import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, Target, ChevronUp, ChevronDown, CheckCircle, Sparkles } from 'lucide-react';
import { QuestStep } from '@/types/quest-types';
import { QuestConfetti } from './QuestConfetti';
import { useQuest } from '@/contexts/QuestContext';

interface QuestCardProps {
  questStep: QuestStep;
  currentStep: number;
  totalSteps: number;
  completedSteps: number[];
  onComplete: () => void; // Now handles navigation to the quest page
  onSkip: () => void;
  isOnCorrectPage?: boolean; // Whether user is currently on the quest step page
}

export const QuestCard: React.FC<QuestCardProps> = ({
  questStep,
  currentStep,
  totalSteps,
  completedSteps,
  onComplete,
  onSkip,
  isOnCorrectPage = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showConfetti, setShowConfetti] = useState(false);
  const [confettiType, setConfettiType] = useState<'progress' | 'final'>('progress');
  const progressPercentage = questStep.id === 9 ? 100 : (completedSteps.length / totalSteps) * 100;

  // Track previous step to detect changes
  const [prevStep, setPrevStep] = useState(currentStep);
  
  // Get quest context to check if last action was skip
  const { questState } = useQuest();
  
  // Trigger confetti when moving to a new step (but not when skipping)
  useEffect(() => {
    // Check if we just completed a step (currentStep increased) and last action was NOT a skip
    if (currentStep > prevStep && prevStep > 0 && !questState.lastActionWasSkip) {
      // Determine confetti type based on what we just completed
      const justCompletedFinalStep = prevStep === totalSteps && currentStep === totalSteps + 1;
      const type = justCompletedFinalStep ? 'final' : 'progress';
      console.log(`[QuestCard] Triggering ${type} confetti: prevStep=${prevStep}, currentStep=${currentStep}, totalSteps=${totalSteps}`);
      setConfettiType(type);
      setShowConfetti(true);
    }
    setPrevStep(currentStep);
  }, [currentStep, prevStep, totalSteps, questState.lastActionWasSkip]);

  // Function to get the appropriate button text
  const getButtonText = () => {
    // Special handling for celebration step
    if (questStep.id === 9) {
      return "Complete";
    }
    
    if (isOnCorrectPage) {
      return "You're in the right place";
    }
    
    // Map quest step titles to page names for better UX
    const pageNameMap: Record<string, string> = {
      'Create Document': 'Document Generator',
      'Generate Research': 'Research Generator', 
      'Generate Image': 'Image Generator',
      'Create Project': 'Project Manager',
      'Add Task': 'Project Tasks',
      'Import Document': 'Project Documents',
      'Import Research': 'Project Resources',
      'Add Team Member': 'Project Settings'
    };
    
    const pageName = pageNameMap[questStep.title] || questStep.title;
    return `Go to ${pageName}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9, y: 20 }}
      transition={{ duration: 0.3 }}
      className="fixed bottom-20 left-6 z-[99999] w-80"
    >
      {/* Confetti Animation Container - positioned relative to card */}
      <QuestConfetti 
        type={confettiType}
        isActive={showConfetti}
        onComplete={() => setShowConfetti(false)}
      />
      
      {/* Quest Card Content */}
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden pointer-events-auto">
      {/* Header with progress */}
      <div className="bg-gradient-to-r from-purple-500 to-blue-600 p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-white/20 rounded-lg">
              {questStep.id === 9 ? (
                <Sparkles className="h-4 w-4 text-white" />
              ) : (
                <Target className="h-4 w-4 text-white" />
              )}
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm">{questStep.title}</h3>
              <p className="text-white/80 text-xs">
                {questStep.id === 9 ? 'Congratulations!' : `Step ${currentStep} of ${totalSteps}`}
              </p>
            </div>
          </div>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 hover:bg-white/20 rounded-lg transition-colors"
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-white" />
            ) : (
              <ChevronUp className="h-4 w-4 text-white" />
            )}
          </button>
        </div>

        {/* Progress bar - hidden for celebration step */}
        {questStep.id !== 9 && (
          <div className="w-full bg-white/20 rounded-full h-2">
            <motion.div
              className="bg-white h-2 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progressPercentage}%` }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            />
          </div>
        )}
      </div>

      {/* Expandable content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="p-4">
              {/* Current step info */}
              <div className="mb-4">
                <p className="text-gray-600 dark:text-gray-400 text-xs leading-relaxed mb-3">
                  {questStep.description}
                </p>
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 mb-4">
                  <p className="text-blue-800 dark:text-blue-200 text-xs font-medium">
                    {questStep.completionAction}
                  </p>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex gap-2">
                <button
                  onClick={onComplete}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-medium py-2 px-3 rounded-lg transition-all duration-200 flex items-center justify-center gap-1.5 text-xs"
                >
                  {getButtonText()}
                  {questStep.id === 9 ? (
                    <CheckCircle className="h-3 w-3" />
                  ) : isOnCorrectPage ? (
                    <CheckCircle className="h-3 w-3" />
                  ) : (
                    <ArrowRight className="h-3 w-3" />
                  )}
                </button>
                
                <button
                  onClick={onSkip}
                  className="px-3 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-medium transition-colors text-xs"
                >
                  Skip Quest
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Collapsed state with button */}
      {!isExpanded && (
        <div className="px-4 py-4">
          {/* Action buttons - same as expanded state */}
          <div className="flex gap-2">
            <button
              onClick={onComplete}
              className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-medium py-2 px-3 rounded-lg transition-all duration-200 flex items-center justify-center gap-1.5 text-xs"
            >
              {getButtonText()}
              {questStep.id === 9 ? (
                <CheckCircle className="h-3 w-3" />
              ) : isOnCorrectPage ? (
                <CheckCircle className="h-3 w-3" />
              ) : (
                <ArrowRight className="h-3 w-3" />
              )}
            </button>
            
            <button
              onClick={onSkip}
              className="px-3 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-medium transition-colors text-xs"
            >
              Skip Quest
            </button>
          </div>
        </div>
      )}
      </div>
    </motion.div>
  );
};